import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/seo/SEO";
import { ArticleCard } from "@/components/content/ArticleCard";
import { blogArticles, blogCategories } from "@/data/sample-content";

const BlogIndex = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showImages, setShowImages] = useState(false);

  const filtered = blogArticles.filter((p) => {
    const matchesQuery = !query || (
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      p.keywords.join(" ").toLowerCase().includes(query.toLowerCase())
    );
    const matchesCategory = !activeCategory || p.category === activeCategory;
    return matchesQuery && matchesCategory;
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    url: typeof window !== "undefined" ? window.location.href : "",
    name: "SOL-VI Blog",
    description: "Actuele inzichten over energieopslag, warmteopslag en duurzame energie.",
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog energieopslag & warmteopslag | SOL-VI"
        description="Diepgaande artikelen over thuisbatterijen, warmteopslag, dynamische tarieven en MKB-oplossingen."
        type="website"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="py-32">
        <div className="container mx-auto flex flex-col items-center gap-16 lg:px-16">
          <div className="text-center">
            <Badge variant="secondary" className="mb-6">
              Laatste Updates
            </Badge>
            <h2 className="mb-3 text-pretty text-3xl font-semibold md:mb-4 md:text-4xl lg:mb-6 lg:max-w-3xl lg:text-5xl">
              Blog Posts
            </h2>
            <p className="mb-8 text-muted-foreground md:text-base lg:max-w-2xl lg:text-lg">
              Actualiteit, uitleg en praktijkcases voor particulieren en MKB: thuisbatterijen, buffervaten, 
              dynamische tarieven en slimme sturing.
            </p>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-sm text-muted-foreground">Afbeeldingen</span>
              <button
                onClick={() => setShowImages((v) => !v)}
                className={`h-8 w-14 rounded-full border transition-colors ${
                  showImages ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
                aria-pressed={showImages}
                aria-label="Schakel afbeeldingen in/uit"
              >
                <span className={`block h-7 w-7 bg-background rounded-full shadow transform transition-transform m-0.5 ${showImages ? "translate-x-6" : "translate-x-0"}`} />
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="mb-8 flex flex-col items-center">
            <div className="flex flex-col sm:flex-row gap-4 mb-6 w-full max-w-2xl">
              <input
                aria-label="Zoek in artikelen"
                placeholder="Zoek op onderwerp..."
                className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2 justify-center">
              <Button 
                variant={activeCategory === null ? "default" : "outline"} 
                onClick={() => setActiveCategory(null)}
                className="rounded-full"
              >
                Alle onderwerpen
              </Button>
              {blogCategories.map((category) => (
                <Button 
                  key={category.key} 
                  variant={activeCategory === category.key ? "default" : "outline"} 
                  onClick={() => setActiveCategory(category.key)}
                  className="rounded-full text-sm"
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {filtered.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                showImages={showImages}
                linkTo={`/blog/${article.slug}`}
              />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogIndex;